import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, curSym, openPdf } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { StatusBadge } from "@finance/components/StatusBadge";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Printer } from "@phosphor-icons/react";

function Bar({ label, pct, color }) {
  const v = Math.min(Math.max(pct || 0, 0), 100);
  return (
    <div data-testid={`bar-${label.toLowerCase()}`}>
      <div className="mb-0.5 flex items-center justify-between text-[11px] text-gray-500">
        <span>{label}</span><span className="lx-num font-medium text-gray-700">{v.toFixed(0)}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full transition-all" style={{ width: `${v}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function PoHealth() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/reports/po-health").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter((r) =>
    !q || (r.lx_po_no || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.supplier_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.client_name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <Layout>
      <PageHeader title="PO Health" subtitle="Received · Delivered · Invoiced — at a glance"
        actions={<Button size="sm" className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="print-po-health" onClick={() => openPdf("pdf/po-health")}><Printer size={15} className="mr-1" />Print</Button>} />
      <div className="p-8">
        <Input className="mb-5 max-w-sm" placeholder="Search PO, supplier or client…" data-testid="po-health-search" value={q} onChange={(e) => setQ(e.target.value)} />
        {loading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-sm border border-dashed border-gray-300 p-10 text-center text-sm text-gray-400">No active POs with ordered value yet.</div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {filtered.map((r) => (
              <div key={r.transaction_id} data-testid={`po-health-card-${r.transaction_id}`}
                onClick={() => navigate(`/transactions/${r.transaction_id}`)}
                className="cursor-pointer rounded-sm border border-gray-200 bg-white p-4 transition-shadow hover:shadow-md">
                <div className="mb-1 flex items-center justify-between">
                  <span className="font-mono-num font-display text-base font-bold text-gray-900">{r.lx_po_no || "No PO number"}</span>
                  <StatusBadge status={r.status} />
                </div>
                <div className="mb-3 truncate text-xs text-gray-500">
                  {r.supplier_name || "—"}{r.client_name ? ` → ${r.client_name}` : ""}
                </div>
                <div className="space-y-2">
                  <Bar label="Received" pct={r.received_pct} color="#122E5C" />
                  <Bar label="Delivered" pct={r.delivered_pct} color="#1B4383" />
                  <Bar label="Invoiced" pct={r.invoiced_pct} color="#1B4383" />
                </div>
                <div className="mt-3 border-t border-gray-100 pt-2 text-right text-xs text-gray-500">
                  Ordered <span className="lx-num font-medium text-gray-800">{curSym(r.currency)} {fmtNum(r.ordered_val)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
